import { Process } from '@/classes/Process'
import { System } from '@/classes/System'
import store from '@/store/store'
import { DailyAggregate } from '@/classes/DailyAggregate'
import { ScheduleHour } from '@/classes/ScheduleHour'

export default {
  name: 'ccHelpers',
  /**
   * @param {response} res
   * @returns ok and json data
   */
  isSuccess: function (res:Response) {
    return res.ok && res.json()
  },
  /**
   * @param {response} res
   * @returns promise rejection
   */
  handleFailure: function (res:Response) {
    return Promise.reject(res)
  },
  /**
   * @param {string} source
   * @returns copy of json object
   */
  cloneJSON: function (source:any):any {
    return JSON.parse(JSON.stringify(source))
  },
  /**
   *
   * @param paid string
   * @param outdoorTemperature number
   * @param signatureIndex number
   * @param signature array of signature values
   * @returns temperature for the specific process, at specified temperature, at specified index
   */
  getSignatureForTemperatureAndProcess: function (paid:string, outdoorTemperature:number, signatureIndex:number, signature:Array<any>):number | any {
    let signatureValueForTemperature = 0

    const signatureForTemperature:any = signature.find(signature =>
      signature.outdoorTemperature === outdoorTemperature &&
      signature.signatureIndex === signatureIndex &&
      signature.paid === paid
    )

    // set signature value
    if (signatureForTemperature) {
      signatureValueForTemperature = signatureForTemperature.energyTot
    }

    return signatureValueForTemperature
  },
  getEnergyProfileForHourAndDay: function (hour:string, dayName:string, schedule:Array<ScheduleHour>):number {
    const parsedHour = parseInt(hour) + 1

    const scheduleHour:ScheduleHour | undefined = schedule.find(scheduleHour =>
      scheduleHour.hour === parsedHour
    )

    if (scheduleHour) {
      return scheduleHour.getIndexForDay(dayName)
    }

    // fallback to returning profile hour index 1
    return -1
  },
  aggregatePerDay: function (rawData:Array<any>, signature:Array<any>, system:System):Array<any> {
    const cleanedData:Array<DailyAggregate> = []

    rawData.forEach(dataPoint => {
      // get schedule for this process
      const processSchedule = system.processes.find(process => process.paid === dataPoint.paid)?.schedule

      // if no schedule found, use index 1
      let energyProfileForHour = -1

      // get this processSignatureIndex from schedule
      if (processSchedule) {
        energyProfileForHour = this.getEnergyProfileForHourAndDay(dataPoint.hour, dataPoint.dayName, processSchedule)
      }

      // get this process signature value for this hour
      const energySignatureValueForHour = this.getSignatureForTemperatureAndProcess(dataPoint.paid, dataPoint.outdoorTemperatureRounded, energyProfileForHour, signature)

      // check id day exist in cleaned data
      let dailyAggregate:DailyAggregate | undefined = cleanedData.find(dailyAggregate => dailyAggregate.smpDate === dataPoint.smpDate)

      if (dailyAggregate) {
        // if day aggregate allready started, append datapoint values to this
        dailyAggregate.increaseEnergies(
          dataPoint.energyComp,
          dataPoint.energyAux,
          dataPoint.energyTot,
          dataPoint.energyCooling,
          dataPoint.energyHeating
        )
        dailyAggregate.increaseSignatureEnergyTot(energySignatureValueForHour)
        dailyAggregate.addOutdoorTemperature(dataPoint.outdoorTemperature)
        dailyAggregate.addTemperatureAndEnergy(dataPoint.paid, dataPoint.hour, dataPoint.outdoorTemperatureRounded, energySignatureValueForHour, energyProfileForHour)
      } else {
        // day aggregate not found, initiate a new one.
        dailyAggregate = new DailyAggregate(
          dataPoint.smpDate,
          dataPoint.dayName,
          dataPoint.outdoorTemperature,
          dataPoint.energyComp,
          dataPoint.energyAux,
          dataPoint.energyTot,
          dataPoint.energyCooling,
          dataPoint.energyHeating
        )

        dailyAggregate.increaseSignatureEnergyTot(energySignatureValueForHour)
        dailyAggregate.addOutdoorTemperature(dataPoint.outdoorTemperature)
        dailyAggregate.addTemperatureAndEnergy(dataPoint.paid, dataPoint.hour, dataPoint.outdoorTemperatureRounded, energySignatureValueForHour, energyProfileForHour)

        // this is an new day, so push it to list
        cleanedData.push(dailyAggregate)
      }
    })

    return cleanedData
  },
  aggregatePerTemperature: function (data:Array<any>):Array<any> {
    const temperatureAggregatedData:Array<any> = []

    // Prepare data for temperature axis instead of time.
    data.forEach(dataPoint => {
      const temperatureDataPoint = temperatureAggregatedData.find(newDataPoint => newDataPoint.outdoorTemperatureRounded === dataPoint.outdoorTemperatureRounded)

      if (!temperatureDataPoint) {
        // no data point found, add one
        temperatureAggregatedData.push({
          outdoorTemperatureRounded: dataPoint.outdoorTemperatureRounded,
          energyComp: dataPoint.energyComp,
          energyAux: dataPoint.energyAux,
          energyTot: dataPoint.energyTot,
          energyCooling: dataPoint.energyCooling,
          energyHeating: dataPoint.energyHeating
        })
      } else {
        // temperature allready exists, add this point's values
        temperatureDataPoint.energyComp += dataPoint.energyComp
        temperatureDataPoint.energyAux += dataPoint.energyAux
        temperatureDataPoint.energyTot += dataPoint.energyTot
        temperatureDataPoint.energyCooling += dataPoint.energyCooling
        temperatureDataPoint.energyHeating += dataPoint.energyHeating
      }
    })

    temperatureAggregatedData.forEach(dataPoint => {
      dataPoint.compressorCoolingCop = parseFloat((dataPoint.energyCooling / dataPoint.energyComp).toFixed(2))
      dataPoint.totalCoolingCop = parseFloat((dataPoint.energyCooling / (dataPoint.energyComp + dataPoint.energyAux)).toFixed(2))
      dataPoint.compressorHeatingCop = parseFloat((dataPoint.energyHeating / dataPoint.energyComp).toFixed(2))
      dataPoint.totalHeatingCop = parseFloat((dataPoint.energyHeating / (dataPoint.energyComp + dataPoint.energyAux)).toFixed(2))
    })

    temperatureAggregatedData.sort((a, b) => {
      return a.outdoorTemperatureRounded - b.outdoorTemperatureRounded
    })

    return temperatureAggregatedData
  },
  aggregatePerUnit: function (rawData:Array<any>, system:System):Array<any> {
    const unitAggregatedData:Array<any> = []

    // Prepare data for temperature axis instead of time.
    rawData.forEach(dataPoint => {
      const unitDataPoint = unitAggregatedData.find(
        existingDataPointForUnit => existingDataPointForUnit.idUnit === dataPoint.idUnit
      )

      if (!unitDataPoint) {
        // no data point found, add one
        unitAggregatedData.push({
          idUnit: dataPoint.idUnit,
          energyComp: dataPoint.energyComp,
          energyAux: dataPoint.energyAux,
          energyTot: dataPoint.energyTot,
          energyCooling: dataPoint.energyCooling,
          energyHeating: dataPoint.energyHeating
        })
      } else {
        // temperature allready exists, add this point's values
        unitDataPoint.energyComp += dataPoint.energyComp
        unitDataPoint.energyAux += dataPoint.energyAux
        unitDataPoint.energyTot += dataPoint.energyTot
        unitDataPoint.energyCooling += dataPoint.energyCooling
        unitDataPoint.energyHeating += dataPoint.energyHeating
      }
    })

    unitAggregatedData.forEach(dataPoint => {
      dataPoint.compressorCoolingCop = parseFloat((dataPoint.energyCooling / dataPoint.energyComp).toFixed(2))
      dataPoint.totalCoolingCop = parseFloat((dataPoint.energyCooling / (dataPoint.energyComp + dataPoint.energyAux)).toFixed(2))
      dataPoint.compressorHeatingCop = parseFloat((dataPoint.energyHeating / dataPoint.energyComp).toFixed(2))
      dataPoint.totalHeatingCop = parseFloat((dataPoint.energyHeating / (dataPoint.energyComp + dataPoint.energyAux)).toFixed(2))
    })

    return unitAggregatedData
  },
  /**
   * @param date string
   * @returns date
   *
   * return date based on string, return now as date if no input
   */
  parseDate: function (date:string):Date {
    if (!date) {
      return new Date()
    } else {
      return new Date(date)
    }
  }
}
